import type { PcgState } from './types';

/**
 * PCG32 (PCG-XSH-RR 64/32), the PRNG of the replay format (spec §16.2).
 * Changing this algorithm requires a replay-format version bump.
 */

const MASK64 = (1n << 64n) - 1n;
const MULTIPLIER = 6364136223846793005n;

/** Stream selectors, so that each purpose draws from an independent sequence. */
export const STREAM_MATCH = 0x4d41544348n; // "MATCH"
export const STREAM_AI = 0x4149n; // "AI"
export const STREAM_CAMPAIGN = 0x43414d50n; // "CAMP"

const hex64 = (n: bigint): string => n.toString(16).padStart(16, '0');

export class Pcg32 {
  private state: bigint;
  private readonly inc: bigint;

  private constructor(state: bigint, inc: bigint) {
    this.state = state & MASK64;
    this.inc = inc & MASK64;
  }

  /** Standard pcg32_srandom_r seeding. */
  static seeded(initState: bigint, initSeq: bigint): Pcg32 {
    const rng = new Pcg32(0n, ((initSeq & MASK64) << 1n) | 1n);
    rng.nextU32();
    rng.state = (rng.state + (initState & MASK64)) & MASK64;
    rng.nextU32();
    return rng;
  }

  static fromState(s: PcgState): Pcg32 {
    return new Pcg32(BigInt(`0x${s.state}`), BigInt(`0x${s.inc}`));
  }

  toState(): PcgState {
    return { state: hex64(this.state), inc: hex64(this.inc) };
  }

  /** Next uniformly distributed unsigned 32-bit integer. */
  nextU32(): number {
    const old = this.state;
    this.state = (old * MULTIPLIER + this.inc) & MASK64;
    const xorshifted = Number((((old >> 18n) ^ old) >> 27n) & 0xffffffffn);
    const rot = Number(old >> 59n);
    return ((xorshifted >>> rot) | (xorshifted << (-rot & 31))) >>> 0;
  }

  /** Unbiased integer in [0, bound). */
  nextBelow(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0 || bound > 0x100000000)
      throw new RangeError(`bound must be an integer in [1, 2^32], got ${bound}`);
    const threshold = (0x100000000 - bound) % bound;
    for (;;) {
      const r = this.nextU32();
      if (r >= threshold) return r % bound;
    }
  }

  /** Uniform float in [0, 1) with 32 bits of randomness. */
  nextFloat(): number {
    return this.nextU32() / 0x100000000;
  }

  /** Next 64-bit value built from two outputs. */
  nextU64(): bigint {
    const hi = BigInt(this.nextU32());
    const lo = BigInt(this.nextU32());
    return (hi << 32n) | lo;
  }

  /** In-place Fisher–Yates shuffle (spec §16.2). */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.nextBelow(i + 1);
      const tmp = items[i] as T;
      items[i] = items[j] as T;
      items[j] = tmp;
    }
    return items;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('cannot pick from an empty list');
    return items[this.nextBelow(items.length)] as T;
  }
}

/** FNV-1a 64-bit hash of a string's UTF-8 bytes. */
export function fnv1a64(text: string): bigint {
  let hash = 0xcbf29ce484222325n;
  const bytes = new TextEncoder().encode(text);
  for (const b of bytes) {
    hash ^= BigInt(b);
    hash = (hash * 0x100000001b3n) & MASK64;
  }
  return hash;
}

/** Converts a user-facing seed (integer or string) into a 64-bit PCG initial state. */
export function seedToBigInt(seed: string | number): bigint {
  if (typeof seed === 'number') {
    if (!Number.isSafeInteger(seed))
      throw new RangeError(`numeric seeds must be safe integers, got ${seed}`);
    return BigInt.asUintN(64, BigInt(seed));
  }
  if (typeof seed !== 'string') throw new TypeError('seed must be a string or an integer');
  return fnv1a64(seed);
}

/** Creates a PRNG for one stream of a seed. */
export function streamFor(seed: string | number, stream: bigint): Pcg32 {
  return Pcg32.seeded(seedToBigInt(seed), stream);
}

/** Parses a 16-hex-digit seed produced by {@link hex64}. */
export function parseHex64(hex: string): bigint {
  if (!/^[0-9a-f]{1,16}$/i.test(hex)) throw new Error(`bad 64-bit hex value: ${hex}`);
  return BigInt(`0x${hex}`);
}

export { hex64 };
